// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { InputBox, Select } from '@imprivata-cloud/components';
import type { FormInstance } from 'antd';
import { Form } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidUrl } from '../utils/validation';
import DesktopLockSection from './DesktopLockSection';

export interface HyperdriveFormValues {
  appUsernameAdMatch: boolean | null;
  interconnectUrl: string;
  // REQ-006 / AC-019: nested per `Form.Item name={['epicAppLockConfig', …]}`.
  // Either sub-key may be absent when the admin has not selected a value.
  epicAppLockConfig?: { sharedWorkstation?: string; privateWorkstation?: string };
}

export enum HyperdriveFieldNames {
  AppUsernameAdMatch = 'appUsernameAdMatch',
  InterconnectUrl = 'interconnectUrl',
}

interface HyperdriveConfigFormProps {
  form: FormInstance<HyperdriveFormValues>;
  appDisplayName: string;
  // AC-024 (REQ-009 / REQ-018 / REQ-019): the SAML metadata section is
  // composed into the form rather than rendered as a sibling, so the
  // form owns the exact vertical position of the section relative to
  // the surrounding fields. The current design slots it between the
  // mapping Select and the Interconnect URL InputBox.
  samlMetadata?: ReactNode;
}

const TRANSLATION_ROOT = 'apps.app-modal';

const HyperdriveConfigForm: React.FC<HyperdriveConfigFormProps> = ({
  form,
  appDisplayName,
  samlMetadata,
}) => {
  const { t } = useTranslation();

  // AntD's DefaultOptionType narrows `value` to string | number, but the
  // underlying rc-select accepts any primitive. Legacy parity (REQ-009)
  // requires boolean values so the form value flows straight into
  // `appConfig.appUsernameAdMatch` without a string→boolean conversion.
  const mappingRuleOptions = [
    { value: true, label: t(`${TRANSLATION_ROOT}.mapping-rule.match-true`) },
    { value: false, label: t(`${TRANSLATION_ROOT}.mapping-rule.match-false`) },
  ] as unknown as DefaultOptionType[];

  return (
    <>
      {/* REQ-018 / AC-016 / AC-027a: Display Name is read-only, bound
          to the integration record, and NOT registered with the AntD
          form (no `name` prop on the wrapping Form.Item). The Form.Item
          wrap is layout-only — it inherits AntD's default 24px bottom
          margin so Display Name sits on the same vertical rhythm as
          the mapping Select and every other field in the modal (F-7). */}
      <Form.Item>
        <InputBox
          label={t(`${TRANSLATION_ROOT}.display-name`)}
          aria-label="display-name"
          value={appDisplayName}
          disabled
        />
      </Form.Item>

      <Form.Item name={HyperdriveFieldNames.AppUsernameAdMatch}>
        <Select
          label={t(`${TRANSLATION_ROOT}.mapping-rule.label`)}
          aria-label="username-mapping-rule"
          options={mappingRuleOptions}
        />
      </Form.Item>

      {samlMetadata}

      <Form.Item
        name={HyperdriveFieldNames.InterconnectUrl}
        validateTrigger="onSubmit"
        rules={[
          {
            validator: (_rule, value: string | undefined) => {
              const matchValue = form.getFieldValue(HyperdriveFieldNames.AppUsernameAdMatch) as
                | boolean
                | null;
              const isEmpty = !value || value.trim() === '';
              if (matchValue === false && isEmpty) {
                return Promise.reject(
                  new Error(t(`${TRANSLATION_ROOT}.errors.interconnect-base-urls-required`)),
                );
              }
              if (!isEmpty && !isValidUrl(value)) {
                return Promise.reject(
                  new Error(t(`${TRANSLATION_ROOT}.errors.interconnect-base-urls-invalid`)),
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <InputBox
          label={t(`${TRANSLATION_ROOT}.interconnect-url.label`)}
          aria-label="interconnect-url-input"
          size="large"
        />
      </Form.Item>

      <DesktopLockSection />
    </>
  );
};

export default HyperdriveConfigForm;
